import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SingleAddMember from './components/SingleAddMember';
import RadioSlider from 'components/RadioSlider';
import { ObjectValues } from 'types';
import BulkAddMember from './components/BulkAddMember';
import { DataRoomMembers } from 'router/subrouters/DataRoom/models/DataRoomMember';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';

import './AddGroupMember.styles.scss';

const ADD_MEMBER_METHOD = {
  SINGLE: 'single',
  BULK: 'bulk',
} as const;

type AddMemberMethod = ObjectValues<typeof ADD_MEMBER_METHOD>;

type AddGroupMemberProps = {
  className?: string;
};

const AddGroupMember: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AddGroupMemberProps
> = (props, ref) => {
  const { className } = props;

  const classes = classNames('add-group-member', className);

  const [method, setMethod] = useState<AddMemberMethod>(
    ADD_MEMBER_METHOD.SINGLE,
  );
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { setBuySideGroups, setSellSideMembers } = useContext(MembersContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const sliderSteps = useMemo(
    () => [
      { value: ADD_MEMBER_METHOD.SINGLE, label: t('single') },
      { value: ADD_MEMBER_METHOD.BULK, label: t('bulk') },
    ],
    [t],
  );

  const isSingleAdd = useMemo(
    () => method === ADD_MEMBER_METHOD.SINGLE,
    [method],
  );

  const onClose = useCallback(() => {
    setMethod(ADD_MEMBER_METHOD.SINGLE);
    drawer?.setOpen(false);
  }, [drawer]);

  const updateMembers = useCallback(
    (data: DataRoomMembers) => {
      const { sellSideMembers, buySideGroups } = data;

      setSellSideMembers(sellSideMembers);
      setBuySideGroups(buySideGroups);
      onClose();
    },
    [onClose, setBuySideGroups, setSellSideMembers],
  );

  if (doesntHaveAccess) return null;

  return (
    <SideDrawer
      ref={drawerRef}
      title={t(isSingleAdd ? 'addMember' : 'addMembers')}
      className={classes}
      closeAction={onClose}
    >
      {drawer?.open && (
        <>
          <RadioSlider slideSteps={sliderSteps} onChange={setMethod} />
          {isSingleAdd ? (
            <SingleAddMember updateMembers={updateMembers} />
          ) : (
            <BulkAddMember updateMembers={updateMembers} />
          )}
        </>
      )}
    </SideDrawer>
  );
};

export default forwardRef(AddGroupMember);
